import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { channelCreate } from "../../../redux/features/Notifications";

import PrimaryStepper from "../../../components/_utils/CommonSteppers/PrimaryStepper";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import ICONS from "../../../assets/Icons";
import SVGComponent from "../../../components/_utils/SVGComponent";
import CommonLayout from "../../../components/_utils/CommonLayout";
import SelectTargetChannel from "../../../components/Channels/SelectTargetChannel";
import EnterChannelNameURL from "../../../components/Channels/EnterChannelNameURL";
import ChannelPasteLogoURL from "../../../components/Channels/ChannelPasteLogoURL";
import CommonToast from "../../../components/_utils/CommonToast";
import SmallPopup from "../../../components/_utils/CommonSmallPopup";
import TEXT from "../../../utils/globalText.js";

const ChannelCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [channelPayLoad, setChannelPayLoad] = useState({
    channelTarget: "",
    channelName: "",
    channelUrl: "",
  });
  const [isCreatingChannel, setIsCreatingChannel] = useState(false);
  const [isChannelCreated, setIsChannelCreated] = useState(false);
  const [isAddChannelBtnDisabled, setIsAddChannelBtnDisabled] = useState(true);
  const [lastFailedPayload, setLastFailedPayload] = useState(null);
  const [apiErrors, setApiErrors] = useState([]);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  const [channelCreationGuide, setChannelCreationGuide] = useState(false);
  const [copyLogoURL, setCopyLogoURL] = useState(TEXT?.COPY_TO_CLIPBOARD);
  const channelOptions = [
    { name: TEXT?.GOOGLE_CHAT_LABEL, value: "gchat", icon: ICONS.GOOGLE_CHAT },
    { name: TEXT?.SLACK, value: "slack", icon: ICONS.SLACK },
    {
      name: TEXT?.MICROSOFT_TEAMS_LABEL,
      value: "teams",
      icon: ICONS.MICROSOFT_TEAMS,
    },
    { name: TEXT?.CUSTOM_WEBHOOK, value: "generic", icon: ICONS.WEB_HOOK },
  ];
  const [showConfirmLeavePopup, setShowConfirmLeavePopup] = useState(false);

  const isFormDirty = () => {
    return Boolean(
      channelPayLoad.channelTarget ||
      channelPayLoad.channelName?.trim() ||
      channelPayLoad.channelUrl?.trim(),
    );
  };

  const handleBack = () => {
    if (isFormDirty()) {
      setShowConfirmLeavePopup(true);
    } else {
      navigate(-1);
    }
  };

  const handleAddChannel = async () => {
    if (isAddChannelBtnDisabled || isCreatingChannel || isChannelCreated) {
      return;
    }

    try {
      setIsCreatingChannel(true);
      setApiErrors([]);

      const payload = {
        name: channelPayLoad.channelName,
        provider: channelPayLoad.channelTarget,
        url: channelPayLoad.channelUrl,
      };

      const response = await dispatch(channelCreate(payload)).unwrap();

      const channelId = response?.channels?.[0]?.id;

      setIsChannelCreated(true);

      if (response?.status && channelId) {
        navigate(
          `/home/notification_dashboard/create_channel/success/${channelId}`,
          {
            replace: true,
          },
        );
      }
    } catch (error) {
      setLastFailedPayload({
        channelTarget: channelPayLoad.channelTarget,
        channelName: channelPayLoad.channelName,
        channelUrl: channelPayLoad.channelUrl,
      });

      const baseErrorList =
        error?.errors?.base ||
        error?.response?.data?.errors?.base ||
        (Array.isArray(error?.base) ? error.base : []);

      const isError =
        error?.status === 422 ||
        error?.statusCode === 422 ||
        error?.response?.status === 422 ||
        error?.status === false ||
        baseErrorList.length > GlobalConfiguration?.lengthCheckZero;

      if (
        isError &&
        baseErrorList.length > GlobalConfiguration?.lengthCheckZero
      ) {
        setApiErrors(baseErrorList);
      } else {
        const errorMessage = error?.message || TEXT?.FAILED_TO_CREATE_CHANNEL;

        CommonToast({
          text: errorMessage,
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
      }

      return error;
    } finally {
      setIsCreatingChannel(false);
    }
  };

  const dataArray = [
    {
      previousText: TEXT?.CANCEL,
      nextText: TEXT?.CREATE,
      previousEnableBgColor: "var(--primary-nav-act)",
      previousDisableBgColor: "var(--primary-nav-act)",
      previousEnableTextColor: "var(--text-primary)",
      previousHoverOption: "white",
      nextHoverOption: "black",
      nextIsEnabled:
        !isAddChannelBtnDisabled && !isCreatingChannel && !isChannelCreated,
      previousIsDisabled: isCreatingChannel,
      nextIsLoading: isCreatingChannel,
      nextLoaderNeeded: true,
      nextEnableBgColor: "var(--positive-act)",
      nextDisableBgColor:
        isCreatingChannel || isChannelCreated
          ? "var(--positive-act)"
          : "var(--text-disabled)",
      nextEnableTextColor: "var(--text-primary)",
      nextDisableTextColor: "var(--text-primary)",
      stepperText: TEXT?.TARGET_CHANNEL_AND_DETAILS,
    },
  ];

  const isValidUrlFormat = (url) => {
    if (!url) return false;
    const strictHttpsRegex =
      /^https:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{2,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/i;
    return strictHttpsRegex.test(url.trim());
  };

  useEffect(() => {
    const { channelTarget, channelName, channelUrl } = channelPayLoad;
    const isUrlValid = isValidUrlFormat(channelUrl);
    const isFormFilled = Boolean(
      channelTarget && channelName?.trim() && isUrlValid,
    );

    const isMatchingFailedPayload =
      lastFailedPayload &&
      channelTarget === lastFailedPayload.channelTarget &&
      channelName.trim() === lastFailedPayload.channelName.trim() &&
      channelUrl.trim() === lastFailedPayload.channelUrl.trim();

    if (
      isFormFilled &&
      !isMatchingFailedPayload &&
      apiErrors.length === GlobalConfiguration?.lengthCheckZero
    ) {
      setIsAddChannelBtnDisabled(false);
    } else {
      setIsAddChannelBtnDisabled(true);
    }
  }, [channelPayLoad, lastFailedPayload, apiErrors]);

  return (
    <div className="w-width-full h-full-height flex items-center flex-col bg-primary-bg overflow-y-auto">
      <div className="w-width-full hidden Qsmd:block h-navbar-breadcrumb-height Qlg:bg-transparent bg-primary-nav flex-shrink-0"></div>

      {showConfirmLeavePopup && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle={TEXT?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="text-primary"
            textSize="font-lg"
            subtext={TEXT?.EXIT_CURRENT_FLOW_NOTE}
            btnOneText={TEXT?.STAY}
            btnOneHandleFunction={() => setShowConfirmLeavePopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText={TEXT?.LEAVE}
            btnTwoHandleFunction={() => {
              setShowConfirmLeavePopup(false);
              navigate(-1);
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoHoverOption="black"
          />
        </div>
      )}

      <div className="fixed Qsmd:relative left-left-base z-z-index-loader flex justify-center items-center flex-col lg:flex-col-reverse w-width-full lg:py-padding-base bottom-bottom-base top-auto lg:top-top-base lg:bottom-auto min-h-stepper-height Qsmd:bg-transparent">
        <PrimaryStepper
          activeStep={GlobalConfiguration?.lengthCheckZero}
          handleForwardAction={handleAddChannel}
          handleBackwardAction={handleBack}
          dataArray={dataArray}
        />
      </div>

      <div className="flex Qsmd:hidden items-center w-full-width h-mobile-create-header bg-primary-nav px-padding-md py-padding-sm top-top-base z-z-index-header shadow-box-shadow-bottom rounded-b-border-radius-sm">
        <div
          className="cursor-pointer"
          onClick={() => {
            handleBack();
          }}
        >
          <SVGComponent
            url={ICONS?.ARROW_LEFT_WHITE}
            color={"var(--text-primary)"}
            height={"var(--icon-sm)"}
            width={"var(--icon-sm)"}
            alt={TEXT?.BACK}
          />
        </div>
        <div className="flex flex-col gap-gap-xs ml-margin-md">
          <p className="text-font-md font-sans text-text-primary">
            {TEXT?.CREATE} {TEXT?.CHANNEL.toLowerCase()}
          </p>
          <p className="text-xs font-sans text-text-primary">
            {TEXT?.TARGET_CHANNEL_AND_DETAILS}
          </p>
        </div>
      </div>
      <div className="overflow-hidden h-full flex flex-col items-center relative w-width-full">
        <div className="w-width-full h-height-full mb-margin-base overflow-y-auto">
          <div className="w-width-full flex flex-col Qlg:px-padding-xs px-padding-md gap-gap-md Qsmd:pt-padding-lg pt-padding-md pb-padding-md max-w-tab-width-normal mx-auto">
            <CommonLayout>
              <div className="w-width-full lg:max-w-[var(--create-box-width)]">
                <SelectTargetChannel
                  logoUrl={ICONS?.XPENTRA_SYMBOL}
                  copyLogoURL={copyLogoURL}
                  setCopyLogoURL={setCopyLogoURL}
                  channelOptions={channelOptions}
                  channelPayLoad={channelPayLoad}
                  setChannelPayLoad={(newVal) => {
                    setApiErrors([]);
                    setChannelPayLoad(newVal);
                  }}
                  channelDropdownOpen={channelDropdownOpen}
                  setChannelDropdownOpen={setChannelDropdownOpen}
                  channelCreationGuide={channelCreationGuide}
                  setChannelCreationGuide={setChannelCreationGuide}
                />
              </div>

              <div className="flex flex-col gap-gap-md">
                <div
                  className={`w-width-full lg:max-w-[var(--create-box-width)] ${
                    !channelPayLoad?.channelTarget
                      ? "opacity-light-opacity pointer-events-none"
                      : "opacity-base-opacity"
                  }`}
                >
                  <EnterChannelNameURL
                    channelPayLoad={channelPayLoad}
                    setChannelPayLoad={(newVal) => {
                      setApiErrors([]);
                      setChannelPayLoad(newVal);
                    }}
                    isValidUrl={isValidUrlFormat(channelPayLoad.channelUrl)}
                    apiErrors={apiErrors}
                  />
                </div>

                {channelPayLoad?.channelTarget === "gchat" && (
                  <div className="w-width-full lg:max-w-[var(--create-box-width)]">
                    <ChannelPasteLogoURL
                      logoUrl={ICONS?.XPENTRA_SYMBOL}
                      copyLogoURL={copyLogoURL}
                      setCopyLogoURL={setCopyLogoURL}
                    />
                  </div>
                )}
              </div>
            </CommonLayout>
            <div className="h-mobile-create-header flex-shrink-0 block Qsmd:hidden"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelCreate;
